<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Button from 'primevue/button'
import Popover from 'primevue/popover'
import SelectButton from 'primevue/selectbutton'
import { dashboard as dashboardApi, organization as organizationApi } from '@/api/endpoints'
import type { Notification } from '@/api/types'
import { useEventText, type EventText } from '@/composables/useEventText'
import { useAuthStore } from '@/stores/auth'
import { formatDateTime } from '@/utils/datetime'
import { formatMoney } from '@/utils/money'

/**
 * The top bar's balance and notifications bell (web-app Header). The balance
 * refreshes every minute and after an activation; notifications every 30 s, with
 * read state kept in this browser (web-app never stored it). The panel says in
 * plain words what happened, on which device, by whom and when, grouped by day;
 * "Muhim" keeps only warnings and alerts, and each item opens its device.
 */

const SEEN_KEY = 'mdm.notificationsSeen'

const { t } = useI18n()
const auth = useAuthStore()
const { describeEvent, groupByDay, when } = useEventText()
const balance = ref<number | null>(null)
const notifications = ref<Notification[]>([])
const seen = ref<number>(readSeen())
const panel = ref<InstanceType<typeof Popover> | null>(null)
const view = ref<'all' | 'important'>('all')

function readSeen(): number {
  try {
    return Number(localStorage.getItem(SEEN_KEY) ?? 0)
  } catch {
    return 0
  }
}

const isNew = (n: Notification) => Date.parse(n.createdAt) > seen.value
const important = (n: Notification) => n.tone === 'DANGER' || n.tone === 'WARNING'
const unread = computed(() => notifications.value.filter(isNew).length)
const importantCount = computed(() => notifications.value.filter(important).length)
const viewOptions = computed(() => [
  { value: 'all', label: t('notifications.all') },
  { value: 'important', label: importantCount.value ? `${t('notifications.important')} · ${importantCount.value}` : t('notifications.important') },
])

interface Item extends EventText {
  deviceId: string | null
  deviceName: string | null
  unread: boolean
}

/** What happened, on which device, by whom and when — newest first, grouped by day. */
const groups = computed(() =>
  groupByDay<Item>(
    notifications.value
      .filter((n) => view.value === 'all' || important(n))
      .map((n) => {
        const text = describeEvent(n)
        // The server knows best how serious it is.
        const tone = n.tone === 'DANGER' ? 'danger' : n.tone === 'WARNING' && text.tone !== 'danger' ? 'warn' : text.tone
        return { ...text, tone, deviceId: n.deviceId, deviceName: n.deviceName, unread: isNew(n) }
      }),
  ),
)

async function loadBalance() {
  if (!auth.currentOrganization) {
    return
  }
  try {
    balance.value = (await organizationApi.get()).balance
  } catch {
    balance.value = null
  }
}

async function loadNotifications() {
  if (!auth.currentOrganization || document.visibilityState !== 'visible') {
    return
  }
  try {
    notifications.value = await dashboardApi.notifications()
  } catch {
    // The bell stays as it was; the screens report errors themselves.
  }
}

/** Everything shown so far counts as read: the badge clears, new arrivals are marked again. */
function markRead() {
  const newest = notifications.value.reduce((max, n) => Math.max(max, Date.parse(n.createdAt)), 0)
  seen.value = Math.max(Date.now(), newest)
  try {
    localStorage.setItem(SEEN_KEY, String(seen.value))
  } catch {
    // Private mode: read until the page reloads.
  }
}

function toggle(event: Event) {
  panel.value?.toggle(event)
}

// The organization arrives after the shell renders; load as soon as it does.
watch(
  () => auth.currentOrganization?.id,
  (orgId) => {
    if (orgId) {
      loadBalance()
      loadNotifications()
    }
  },
  { immediate: true },
)

let balanceTimer: number | undefined
let bellTimer: number | undefined
onMounted(() => {
  balanceTimer = window.setInterval(loadBalance, 60_000)
  bellTimer = window.setInterval(loadNotifications, 30_000)
  window.addEventListener('mdm:balance', loadBalance)
})
onBeforeUnmount(() => {
  window.clearInterval(balanceTimer)
  window.clearInterval(bellTimer)
  window.removeEventListener('mdm:balance', loadBalance)
})
</script>

<template>
  <template v-if="auth.currentOrganization">
    <RouterLink v-if="balance !== null && auth.canManageOrganization" to="/balance" class="status__balance" :title="t('header.balance')">
      {{ formatMoney(balance) }}
    </RouterLink>
    <span v-else-if="balance !== null" class="status__balance" :title="t('header.balance')">{{ formatMoney(balance) }}</span>
    <span class="status__bell">
      <Button
        icon="pi pi-bell"
        severity="secondary"
        text
        size="small"
        :aria-label="unread ? t('notifications.bellUnread', { n: unread }) : t('notifications.title')"
        aria-haspopup="dialog"
        @click="toggle"
      />
      <span v-if="unread" class="status__badge" aria-hidden="true">{{ unread > 9 ? '9+' : unread }}</span>
    </span>
    <!-- Closing the panel marks what was in it as read; until then new items stay highlighted. -->
    <Popover ref="panel" class="notify-popover" @hide="unread && markRead()">
      <section class="notify" :aria-label="t('notifications.title')">
        <header class="notify__head">
          <div class="notify__title">
            <strong>{{ t('notifications.title') }}</strong>
            <span v-if="unread" class="notify__count">{{ t('notifications.newCount', { n: unread }) }}</span>
          </div>
          <Button
            v-if="unread"
            :label="t('notifications.markRead')"
            icon="pi pi-check"
            severity="secondary"
            text
            size="small"
            @click="markRead"
          />
        </header>
        <SelectButton
          v-if="notifications.length"
          v-model="view"
          :options="viewOptions"
          option-label="label"
          option-value="value"
          :allow-empty="false"
          size="small"
          class="notify__tabs"
        />

        <div class="notify__body">
          <section v-for="group in groups" :key="group.day" class="notify__day">
            <h4 class="notify__day-label">{{ group.label }}</h4>
            <ul class="notify__list">
              <li v-for="item in group.items" :key="item.id">
                <component
                  :is="item.deviceId ? 'RouterLink' : 'div'"
                  :to="item.deviceId ? `/devices/${item.deviceId}` : undefined"
                  :class="['notify__item', { 'notify__item--new': item.unread }]"
                  @click="item.deviceId && panel?.hide()"
                >
                  <span :class="['notify__icon', `notify__icon--${item.tone}`]"><i :class="['pi', item.icon]" aria-hidden="true" /></span>
                  <span class="notify__text">
                    <span class="notify__line">
                      <span class="notify__name">{{ item.title }}</span>
                      <time class="notify__time" :datetime="item.at" :title="formatDateTime(item.at)">{{ when(item.at) }}</time>
                    </span>
                    <span class="notify__detail">
                      <span v-if="item.deviceName" class="notify__device">{{ item.deviceName }}</span>
                      <span v-if="item.detail">{{ item.detail }}</span>
                    </span>
                    <span v-if="item.by" class="notify__by"><i class="pi pi-user" aria-hidden="true" />{{ item.by }}</span>
                  </span>
                  <span v-if="item.unread" class="notify__dot" :aria-label="t('notifications.unread')" />
                </component>
              </li>
            </ul>
          </section>

          <div v-if="!groups.length" class="notify__empty">
            <i class="pi pi-bell-slash" aria-hidden="true" />
            <span>{{ view === 'important' ? t('notifications.emptyImportant') : t('notifications.empty') }}</span>
          </div>
        </div>
      </section>
    </Popover>
  </template>
</template>

<style scoped>
.status__balance { font: var(--text-body-medium); font-variant-numeric: tabular-nums; color: var(--text-primary); padding: 0 var(--space-8); }
a.status__balance:hover { text-decoration: underline; }
.status__bell { position: relative; display: inline-flex; }
.status__badge {
  position: absolute; top: 0; right: 0; min-width: 16px; height: 16px; padding: 0 4px; border-radius: 8px;
  background: var(--status-failed); color: var(--text-on-fill); font-size: 10px; line-height: 16px; text-align: center; pointer-events: none;
}
.notify { display: flex; flex-direction: column; width: min(25rem, calc(100vw - 32px)); max-height: min(32rem, calc(100vh - 96px)); }
.notify__head { display: flex; align-items: center; justify-content: space-between; gap: var(--space-8); padding: var(--space-4) 0 var(--space-8); }
.notify__title { display: flex; align-items: baseline; gap: var(--space-8); }
.notify__title strong { font: var(--text-section); }
.notify__count { font: var(--text-small-medium); color: var(--status-failed); }
.notify__tabs { align-self: flex-start; margin-bottom: var(--space-8); }

.notify__body { overflow-y: auto; margin: 0 calc(-1 * var(--space-8)); padding: 0 var(--space-8); overscroll-behavior: contain; }
.notify__day + .notify__day { margin-top: var(--space-8); }
.notify__day-label {
  position: sticky; top: 0; z-index: 1; margin: 0; padding: var(--space-6) 0; background: var(--surface-page);
  font-size: 11px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.notify__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }

.notify__item {
  display: grid; grid-template-columns: 30px minmax(0, 1fr) 8px; gap: var(--space-10, 10px); align-items: start;
  padding: var(--space-8); border-radius: var(--radius-8); color: inherit; text-decoration: none;
}
a.notify__item:hover, a.notify__item:focus-visible { background: var(--surface-subtle); text-decoration: none; outline: none; }
.notify__item--new { background: color-mix(in srgb, var(--status-delivered) 7%, transparent); }

.notify__icon {
  display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 50%;
  font-size: 13px; background: color-mix(in srgb, currentColor 14%, transparent);
}
.notify__icon--success { color: var(--status-done); }
.notify__icon--danger { color: var(--status-failed); }
.notify__icon--warn { color: var(--status-attention-fg); }
.notify__icon--info { color: var(--status-delivered); }
.notify__icon--neutral { color: var(--text-secondary); }

.notify__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.notify__line { display: flex; align-items: baseline; justify-content: space-between; gap: var(--space-8); }
.notify__name { font-weight: 500; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.notify__item--new .notify__name { font-weight: 600; }
.notify__time { flex-shrink: 0; font-size: 12px; color: var(--text-tertiary); font-variant-numeric: tabular-nums; white-space: nowrap; }
.notify__detail { display: flex; flex-wrap: wrap; gap: 0 var(--space-6); font-size: 13px; color: var(--text-secondary); overflow-wrap: anywhere; }
.notify__detail > * + *::before { content: '·'; margin-right: var(--space-6); color: var(--text-tertiary); }
.notify__device { color: var(--text-primary); }
.notify__by { display: inline-flex; align-items: center; gap: var(--space-4); font-size: 12px; color: var(--text-tertiary); }
.notify__by .pi { font-size: 10px; }
.notify__dot { width: 8px; height: 8px; margin-top: 7px; border-radius: 50%; background: var(--status-delivered); }

.notify__empty {
  display: flex; flex-direction: column; align-items: center; gap: var(--space-8); padding: var(--space-32, 32px) 0;
  color: var(--text-tertiary);
}
.notify__empty .pi { font-size: 24px; }
</style>
